import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Cms } from '@impact/data';
import { Observable, Subject, takeUntil } from 'rxjs';
import { chevronDownIcon } from '../../../icons';
import { NavigationService } from '../navigation.service';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
    selector: 'app-side-navigation',
    template: `
       <ng-container [ngSwitch]="isDesktop">
            <ng-container *ngSwitchCase="true">
                <nav *ngIf="currentSubtree$ | async as currentSubtree">
                    <h5>
                        <a
                            class="side-navigation-link"
                            [innerText]="currentSubtree.name"
                            [routerLink]="currentSubtree.url"
                        ></a>
                    </h5>
                    <ul class="side-navigation-list">
                        <li *ngFor="let subLevel of currentSubtree.children">
                            <app-expansion-panel
                                class="side-navigation-sub-level"
                                *ngIf="subLevel.includeInNavigation"
                                [expanded]="subLevel | isExpanded: router.url"
                            >
                                <a
                                    class="side-navigation-link side-navigation-sub-level__link"
                                    expansion-title
                                    [class.side-navigation-link--active]="subLevel | isActive: router.url"
                                    [innerText]="subLevel.name"
                                    [routerLink]="subLevel.url"
                                ></a>

                                <ng-container expansion-icon  *ngIf="subLevel.children?.length">
                                    ${chevronDownIcon}
                                </ng-container>

                                <ul class="side-navigation-list">
                                    <ng-container *ngFor="let subSubLevel of subLevel.children">
                                        <li *ngIf="subSubLevel.includeInNavigation">
                                            <a
                                                class="side-navigation-link side-navigation-sub-sub-level"
                                                [class.side-navigation-link--active]="subSubLevel | isActive: router.url"
                                                [innerText]="subSubLevel.name"
                                                [routerLink]="subSubLevel.url"
                                            ></a>
                                        </li>
                                    </ng-container>
                                </ul>
                            </app-expansion-panel>
                        </li>
                    </ul>
                </nav>
            </ng-container>
            <ng-container *ngSwitchCase="false">
                <nav *ngIf="currentSubtree$ | async as currentSubtree">
                    <app-expansion-panel
                        class="side-navigation-sub-level parent-nav"
                        [expanded]="false"
                    >
                        <a
                            class="side-navigation-link is-mobile"
                            expansion-title
                            [class.side-navigation-link--active]="currentSubtree | isActive: router.url"
                            [innerText]="activeNodeName"
                            [routerLink]="router.url"
                        ></a>

                        <ng-container expansion-icon *ngIf="currentSubtree.children?.length">
                            ${chevronDownIcon}
                        </ng-container>

                        <ul class="side-navigation-list">
                            <li *ngFor="let subLevel of currentSubtree.children">
                                <app-expansion-panel
                                    class="side-navigation-sub-level"
                                    *ngIf="subLevel.includeInNavigation"
                                    [expanded]="subLevel | isExpanded: router.url"
                                >
                                    <a
                                        class="side-navigation-link side-navigation-sub-level__link"
                                        expansion-title
                                        [class.side-navigation-link--active]="subLevel | isActive: router.url"
                                        [innerText]="subLevel.name"
                                        [routerLink]="subLevel.url"
                                    >
                                    </a>

                                    <ng-container expansion-icon *ngIf="subLevel.children?.length">
                                        ${chevronDownIcon}
                                    </ng-container>

                                    <ul class="side-navigation-list">
                                        <ng-container *ngFor="let subSubLevel of subLevel.children">
                                            <li *ngIf="subSubLevel.includeInNavigation">
                                                <a
                                                    class="side-navigation-link side-navigation-sub-sub-level"
                                                    [class.side-navigation-link--active]="subSubLevel | isActive: router.url"
                                                    [innerText]="subSubLevel.name"
                                                    [routerLink]="subSubLevel.url"
                                                ></a>
                                            </li>
                                        </ng-container>
                                    </ul>
                                </app-expansion-panel>
                            </li>
                        </ul>
                    </app-expansion-panel>
                </nav>
            </ng-container>
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavigationComponent implements OnInit {
    currentSubtree$ = this.navigationService.getCurrentSubTree();
    private readonly unsubscribe = new Subject<void>();

    isDesktop: boolean;
    activeNodeName: string = '';

    constructor(
        public readonly router: Router,
        private readonly navigationService: NavigationService,
        private breakpointObserver: BreakpointObserver,
        private cdr: ChangeDetectorRef
    ) {}
    ngOnInit() {
        const layoutChanges$ = this.breakpointObserver.observe(['(min-width: 460px)']);

        layoutChanges$.pipe(takeUntil(this.unsubscribe)).subscribe((result) => {
            if (result.matches) {
                this.isDesktop = true;
            } else {
                this.isDesktop = false;
            }
            this.cdr.markForCheck();
        });

        this.currentSubtree$.subscribe((tree) => {
            if (tree) {
                this.activeNodeName = this.getActiveNodeName(tree, this.router.url) || tree.name;
            } else {
                this.activeNodeName = ''; // or a fallback like 'Menu'
            }
        });
    }

    getActiveNodeName(node: Cms.NavigationDto, currentUrl: string): string {
        if (node.url === currentUrl) {
            return node.name;
        }

        if (node.children?.length) {
            for (const child of node.children) {
                const result = this.getActiveNodeName(child, currentUrl);
                if (result) return result;
            }
        }

        return '';
    }

}
